// ─── Pure form-level utilities ────────────────────────────────────────────────
// No component state, no hooks — safe to call from anywhere.
import logger from '../../../utils/logger';

/**
 * Formats a number with thousand separators and a fixed decimal precision.
 * Returns "Invalid number" for NaN / non-numeric input so callers can detect
 * and discard the result rather than displaying a confusing string.
 */
export function formatNumberWithDecimal(number, decimalPlaces) {
  if (typeof number !== 'number' || isNaN(number)) {
    return 'Invalid number';
  }
  return number.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * Strips thousand-separator commas from a formatted number string.
 * Returns "Invalid input" for non-string input so callers can detect the error.
 */
export function removeThousandSeparator(formattedNumber) {
  if (typeof formattedNumber !== 'string') {
    return 'Invalid input';
  }
  return formattedNumber.replace(/,/g, '');
}

/** Promise-based delay helper. */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Normalises a date string to YYYY-MM-DD.
 * Accepts ISO (YYYY-MM-DD), dash-separated (DD-MM-YYYY), and
 * slash-separated (DD/MM/YYYY) inputs.
 */
export function formatDateToYYYYMMDD(dateString) {
  if (!dateString) return dateString;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  const separator = dateString.indexOf('-') !== -1 ? '-' : '/';
  const parts     = dateString.split(separator);
  const day        = parts[0].padStart(2, '0');
  const month      = parts[1].padStart(2, '0');
  const year       = parts[2];
  return `${year}-${month}-${day}`;
}

/**
 * Compares two date strings using the given operator.
 * Returns false (rather than undefined) when the condition is unrecognised.
 */
export function areDatesMatching(dateStr1, dateStr2, condition) {
  const parsedDate2 = formatDateToYYYYMMDD(dateStr2);
  const t1 = new Date(dateStr1).getTime();
  const t2 = new Date(parsedDate2).getTime();
  switch (condition) {
    case '==': return t1 === t2;
    case '>=': return t1 >= t2;
    case '<=': return t1 <= t2;
    case '>':  return t1 >  t2;
    case '<':  return t1 <  t2;
    default:
      if (process.env.NODE_ENV !== 'production') {
        logger.warn(`areDatesMatching: unrecognised condition "${condition}"`);
      }
      return false;
  }
}
